import { KeywordCloud } from "Explore/Components";
import {
  getVenueKeywordsByID,
  getVenueProfileSummaryByID,
  getVenueTopAuthorsByID,
  getVenueTopInstitutionsByID,
} from "Explore/Services/Venue";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AcademicInformation, BasicInformation } from "./Components";
import styles from "./VenueDetail.module.scss";
import VenueSummary from "./Components/VenueSummary";
import { Container } from "@mui/material";
import {
  TIME_FILTER_OPTIONS,
  TIME_FILTER_TYPE,
} from "Explore/Config/constraint";
import useFilterAndPagination from "Hooks/useFilterAndPagination";
import { ParamsFilterKey } from "Config/filterSearchParams";
import { Helmet } from "react-helmet-async";

const VenueDetail = () => {
  const { filter, updateSearchParams } = useFilterAndPagination();
  const venue_id = useLocation().pathname.split("/")[3];
  const [summary, setSummary] = useState({ data: {}, isLoading: true });
  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    totalRows: 10,
  });
  const [topScholars, setTopScholars] = useState({ data: [], isLoading: true });
  const [topInstitutions, setTopInstitutions] = useState({
    data: [],
    isLoading: true,
  });
  const [tab, setTab] = useState("topScholars");

  //concept word cloud
  const [concept, setConcept] = useState({ data: [], isLoading: true });
  const [granularityLevel, setGranularityLevel] = useState(1);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [selectedKeywordsQueue, setSelectedKeywordsQueue] = useState([]);

  //slider
  const [timeFilter, setTimeFilter] = useState(TIME_FILTER_OPTIONS[0].value);

  //init data - slider
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
    getVenueProfileSummaryByID(venue_id, (res) => {
      setSummary({ data: res, isLoading: false });
    });
  }, []);

  useEffect(() => {
    updateSearchParams({
      [ParamsFilterKey.TIME_FILTER]: timeFilter,
    });
  }, [timeFilter]);

  useEffect(() => {
    updateSearchParams({
      [ParamsFilterKey.KEYWORD_QUERY]: selectedKeywords.map((keyword) => {
        return keyword.value;
      }),
    });
  }, [selectedKeywords]);

  useEffect(() => {
    if (selectedKeywordsQueue[selectedKeywordsQueue.length - 1]) {
      let params = new URLSearchParams();
      selectedKeywordsQueue[selectedKeywordsQueue.length - 1].forEach(
        (keyword) => {
          if (keyword) {
            params.append("rootConcept", keyword.real_id);
          }
        }
      );
      getVenueKeywordsByID(
        venue_id,
        timeFilter,
        granularityLevel,
        params.toString(),
        (res) => {
          setConcept({ data: res.keywords, isLoading: false });
        }
      );
    } else {
      getVenueKeywordsByID(
        venue_id,
        timeFilter,
        granularityLevel,
        null,
        (res) => {
          setConcept({ data: res.keywords, isLoading: false });
        }
      );
    }
  }, [timeFilter, granularityLevel]);

  useEffect(() => {
    if (tab == "topScholars") {
      setTopScholars({ ...topScholars, isLoading: true });
      getVenueTopAuthorsByID(
        venue_id,
        pagination.page - 1,
        pagination.rowsPerPage,
        filter[ParamsFilterKey.QUERY],
        timeFilter,
        selectedKeywords,
        (res) => {
          setTopScholars({ data: res.results, isLoading: false });
          if (res.total >= 0) {
            setPagination({ ...pagination, totalRows: res.total });
          }
        }
      );
    }
  }, [
    filter,
    selectedKeywords,
    timeFilter,
    pagination.page,
    pagination.rowsPerPage,
    tab,
  ]);

  useEffect(() => {
    if (tab == "topInstitutions") {
      setTopInstitutions({ ...topInstitutions, isLoading: true });
      getVenueTopInstitutionsByID(
        venue_id,
        pagination.page - 1,
        pagination.rowsPerPage,
        selectedKeywords,
        timeFilter,
        (res) => {
          setTopInstitutions({ data: res.results, isLoading: false });
          if (res.total >= 0) {
            setPagination({ ...pagination, totalRows: res.total });
          }
        }
      );
    }
  }, [
    timeFilter,
    selectedKeywords,
    pagination.page,
    pagination.rowsPerPage,
    tab,
  ]);

  const currentUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  const venueName = summary.data?.name;
  
  const generateTitle = () => {
    if (!summary.data?.name) return "Venue Profile | Academic Explorer";
    const name = summary.data.name;
    const suffix = " | Academic Online";
    const maxLength = 60;

    let titleFull = `${name} — Journal & Conference Details`;
    if ((titleFull + suffix).length <= maxLength) return titleFull + suffix;

    if ((name + suffix).length <= maxLength) return name + suffix;

    return `${name.substring(0, 40)}...${suffix}`;
  };
  
  const generateDescription = () => {
    const defaultDesc = "View detailed venue profile including top scholars, publications, institutions, and research keyword trends.";
    const venueName = summary.data?.name;
    if (!venueName) return defaultDesc;

    const maxLength = 160;

    let descFull = `Explore ${venueName}'s profile including top scholars, publications, institutions, and research keyword trends.`;
    if (descFull.length <= maxLength) return descFull;

    let descShort = `Explore ${venueName}'s profile including top scholars, publications, and research keyword trends.`;
    if (descShort.length <= maxLength) return descShort;

    return `${descShort.substring(0, 157)}...`;
  };

  return (
    <div className={styles["venue-detail"]}>
      <Helmet>
        <title>{generateTitle()}</title>
        <meta name="description" content={generateDescription()} />
        <link rel="canonical" href={currentUrl} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={generateTitle()} />
        <meta property="og:description" content={generateDescription()} />
      </Helmet>
      <BasicInformation summary={summary} />
      <div>
        <Container maxWidth="lg" className={styles["venue-detail-word-map"]}>
          <VenueSummary summary={summary} />
          <div className={styles["keyword-cloud"]}>
            <KeywordCloud
              concept={concept}
              selectedKeywords={selectedKeywords}
              setSelectedKeywords={setSelectedKeywords}
              granularityLevel={granularityLevel}
              setGranularityLevel={setGranularityLevel}
              selectedKeywordsQueue={selectedKeywordsQueue}
              setSelectedKeywordsQueue={setSelectedKeywordsQueue}
              timeFilter={timeFilter}
              setTimeFilter={setTimeFilter}
              timeFilterType={TIME_FILTER_TYPE.DROPDOWN}
              chart_width={
                window.screen.width < 1024
                  ? window.screen.width < 480
                    ? 380
                    : 500
                  : ""
              }
            />
          </div>
        </Container>
      </div>
      <div>
        <Container maxWidth="lg" className={styles["top-result"]}>
          <AcademicInformation
            disabledInfomation={summary.data}
            topInstitutions={topInstitutions}
            topScholars={topScholars}
            tab={tab}
            setTab={setTab}
            pagination={pagination}
            setPagination={setPagination}
            venueId={venue_id}
            timeFilter={timeFilter}
            selectedKeywords={selectedKeywords}
          />
        </Container>
      </div>
    </div>
  );
};

export default VenueDetail;
